import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  StarIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';

const AdminReviews = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: reviewsData, isLoading } = useQuery(
    ['admin-reviews', typeFilter, searchTerm],
    async () => {
      // Since there's no dedicated admin reviews endpoint, we'll fetch from products and vendors
      const [productsRes, vendorsRes] = await Promise.all([
        api.get('/products').then(res => res.data.data),
        api.get('/vendors').then(res => res.data.data)
      ]);

      let allReviews = [];

      // Get product reviews
      if (!typeFilter || typeFilter === 'product') {
        for (const product of productsRes.data || []) {
          try {
            const reviews = await api.get(`/products/${product.id}/reviews`).then(res => res.data.data);
            allReviews = [...allReviews, ...reviews.map(r => ({ ...r, type: 'product', item: product }))];
          } catch (error) {
            // Skip if no reviews
          }
        }
      }

      // Get vendor reviews
      if (!typeFilter || typeFilter === 'vendor') {
        for (const vendor of vendorsRes.data || []) {
          try {
            const reviews = await api.get(`/vendors/${vendor.id}/reviews`).then(res => res.data.data);
            allReviews = [...allReviews, ...reviews.map(r => ({ ...r, type: 'vendor', item: vendor }))];
          } catch (error) {
            // Skip if no reviews
          }
        }
      }

      // Filter by search term
      if (searchTerm) {
        allReviews = allReviews.filter(review => 
          review.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          review.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          review.item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          review.item?.store_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Sort by date
      allReviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      return allReviews;
    },
    {
      onError: (error) => {
        toast.error('خطأ في جلب المراجعات');
      }
    }
  );

  const deleteReviewMutation = useMutation(
    ({ reviewId, type }) => {
      // Note: You'll need to add delete endpoints in the backend
      return api.delete(`/${type}s/reviews/${reviewId}`);
    },
    {
      onSuccess: () => {
        toast.success('تم حذف المراجعة بنجاح');
        queryClient.invalidateQueries('admin-reviews');
      },
      onError: () => {
        toast.error('خطأ في حذف المراجعة');
      }
    }
  );

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= rating ? (
            <StarIconSolid key={star} className="w-5 h-5 text-yellow-400" />
          ) : (
            <StarIcon key={star} className="w-5 h-5 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <StarIcon className="w-8 h-8 text-primary-600 ml-3" />
                <h1 className="text-3xl font-bold text-gray-900">إدارة المراجعات</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                البحث
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="اسم المستخدم أو المنتج..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>

            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                نوع المراجعة
              </label>
              <div className="relative">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 appearance-none"
                >
                  <option value="">جميع الأنواع</option>
                  <option value="product">مراجعات المنتجات</option>
                  <option value="vendor">مراجعات البائعين</option>
                </select>
                <FunnelIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <div className="space-y-4">
          {reviewsData?.map((review) => (
            <div key={`${review.type}-${review.id}`} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Review Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-semibold">
                        {review.user?.name?.charAt(0) || '؟'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{review.user?.name || 'مستخدم محذوف'}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(review.created_at).toLocaleDateString('ar-SA')}
                      </p>
                    </div>
                    <span className={`mr-auto px-3 py-1 text-xs font-semibold rounded-full ${
                      review.type === 'product' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {review.type === 'product' ? 'مراجعة منتج' : 'مراجعة بائع'}
                    </span>
                  </div>

                  {/* Item Info */}
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      {review.type === 'product' ? 'المنتج:' : 'البائع:'}
                    </p>
                    <p className="font-medium text-gray-900">
                      {review.item?.name || review.item?.store_name || 'غير متوفر'}
                    </p>
                  </div>

                  {/* Rating */}
                  <div className="mb-3">
                    {renderStars(review.rating)}
                  </div>

                  {/* Comment */}
                  {review.comment && (
                    <p className="text-gray-700 mb-3">{review.comment}</p>
                  )}

                  {/* Images */}
                  {review.images && review.images.length > 0 && (
                    <div className="flex gap-2 mb-3">
                      {review.images.map((image, index) => (
                        <img
                          key={index}
                          src={image.url}
                          alt={`صورة ${index + 1}`}
                          className="w-20 h-20 rounded object-cover"
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <button
                  onClick={() => {
                    if (window.confirm('هل أنت متأكد من حذف هذه المراجعة؟')) {
                      deleteReviewMutation.mutate({ reviewId: review.id, type: review.type });
                    }
                  }}
                  className="mr-4 text-red-600 hover:text-red-800"
                  title="حذف المراجعة"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {reviewsData?.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <StarIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">لا توجد مراجعات</p>
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminReviews;
